import React from 'react';
import { cn } from '@lib/utils';
import { useHorizontalScrollFade } from '@hooks/useHorizontalScrollFade';
import { Badge } from './badge';

// Rings removed entirely (per design): override Badge's base `focus:ring-*` to 0 and omit any
// `focus-visible:ring` so no focus ring shows on tap OR keyboard. `cn()`'s tailwind-merge lets
// `focus:ring-0` win over the Badge's `focus:ring-2`.
const pillBase =
  'rounded-[32px] font-medium whitespace-nowrap transition-colors cursor-pointer select-none focus:outline-hidden focus:ring-0 focus:ring-offset-0 border-0 flex items-center gap-2';

export interface FilterChipItem<V extends string = string> {
  value: V;
  label: string;
  icon?: React.ReactNode;
  /**
   * The chip's accessible name when the label it shows can be cut short (`appearance` `'outline'`
   * truncates a long label with an ellipsis): the full text. Defaults to the label.
   */
  accessibleLabel?: string;
  /**
   * Text after the label that is never cut (`'outline'`): a long label truncates before it, so it stays whole, e.g.
   * the voter count " · 3" after a poll option. Its own leading space, if any, is kept.
   */
  trailing?: string;
}

interface FilterChipsProps<V extends string = string> {
  items: FilterChipItem<V>[];
  value: V;
  onChange: (value: V) => void;
  ariaLabel: string;
  /**
   * `'standalone'` (default): full-bleed edge-to-edge scrolling row — for a standalone filter
   * row (Spaces categories, global events). `'inline'`: compact cluster for sharing a row with
   * another element, e.g. the events day-header. Both keep the horizontal scroll and the edge
   * fade-mask when the row overflows; only `'standalone'` adds the negative-margin full-bleed.
   */
  density?: 'standalone' | 'inline';
  /**
   * `'filled'` (default): today's chips (periwinkle selected, beige otherwise). `'outline'`: the
   * poll "Who answered" drawer's chips (TECH-197 polls-B6): 32px tall, white with a border, the
   * selected one soft yellow; a long label ends with "…" (full text in `accessibleLabel`); each
   * keeps a 44px hit area (7px each way on the 30px padding box, inside the 1px border), so the
   * row adds 6px above and below, inside its scroll box.
   */
  appearance?: 'filled' | 'outline';
}

const outlineChip =
  "relative h-8 max-w-56 border px-3.5 py-0 text-xs leading-4 text-neutral-800 before:absolute before:inset-x-0 before:-inset-y-[7px] before:content-['']";

/**
 * Horizontally scrollable single-select chip row. Extracted from CommunityList's
 * SpaceCategoryChips so the Spaces category filter and the events filter share one
 * component (active = bg-mb-filter-selected, inactive = bg-mb-beige). Adds an
 * optional leading icon per chip. Generic over the chip value so a caller with a
 * closed union (e.g. EventListView) keeps compile-time checking end to end instead
 * of casting at the boundary. Not memoized: both callers build `items` inline each
 * render (so a memo never hit), and the React Compiler auto-memoizes anyway.
 */
export function FilterChips<V extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  density = 'standalone',
  appearance = 'filled',
}: FilterChipsProps<V>) {
  const isInline = density === 'inline';
  const isOutline = appearance === 'outline';
  const { scrollRef, fadeStyle } = useHorizontalScrollFade(items.length);

  return (
    <div
      ref={scrollRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'flex items-center gap-2 overflow-x-auto no-scrollbar scroll-fade-x',
        !isInline && 'mt-1 mb-1 -mx-4 px-4 pb-0.5',
        isOutline && 'py-1.5',
      )}
      style={fadeStyle}
    >
      {items.map(item => {
        const active = value === item.value;
        return (
          <Badge
            key={item.value}
            variant="outline"
            role="button"
            tabIndex={0}
            onClick={() => {
              onChange(item.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(item.value);
              }
            }}
            className={
              isOutline
                ? cn(
                    pillBase,
                    outlineChip,
                    active
                      ? 'border-mb-yellow-dark/40 bg-mb-yellow-soft/60 font-medium'
                      : 'border-slate-200 bg-white font-normal hover:bg-slate-50',
                  )
                : `${pillBase} ${isInline ? 'h-7 px-3 py-1 text-xs sm:h-8 sm:px-4 sm:py-2 sm:text-sm' : 'h-8 px-4 py-2 text-sm'} text-neutral-700 ${active ? 'bg-mb-filter-selected' : 'bg-mb-beige hover:opacity-80'}`
            }
            aria-pressed={active}
            aria-label={item.accessibleLabel}
          >
            {item.icon ? (
              <span
                className={`${isInline ? 'w-3 h-3 sm:w-3.5 sm:h-3.5' : 'w-3.5 h-3.5'} shrink-0 flex items-center`}
              >
                {item.icon}
              </span>
            ) : null}
            {isOutline ? (
              <span className="flex min-w-0">
                <span className="min-w-0 truncate">{item.label}</span>
                {item.trailing ? (
                  <span className="shrink-0 whitespace-pre">{item.trailing}</span>
                ) : null}
              </span>
            ) : (
              <>
                {item.label}
                {item.trailing}
              </>
            )}
          </Badge>
        );
      })}
    </div>
  );
}
